import { normalizeMediaUrl } from "./productService";

export function getPackDisplayImage(pack, fallback) {
  return pack?.picture || pack?.image || pack?.imgURL || fallback || "";
}

export function getPackDisplayPrice(pack) {
  const raw = pack?.pack_price ?? pack?.price ?? 0;
  return typeof raw === "number" ? raw : parseFloat(String(raw || "0")) || 0;
}

/** Line total for one included pack product using the selected size price when available. */
export function getPackItemLinePrice(entry, selection) {
  const qty = Math.max(1, Number(entry.quantity) || 1);
  const selectedUnit = Number(selection?.unit_price);
  if (Number.isFinite(selectedUnit) && selectedUnit > 0) {
    return selectedUnit * qty;
  }

  const selectedSize = entry.product?.product_sizes?.find(
    (size) => String(size.id) === String(selection?.productSizeId || "")
  );
  const sizeUnit = Number(selectedSize?.price);
  if (Number.isFinite(sizeUnit) && sizeUnit > 0) {
    return sizeUnit * qty;
  }

  const packUnit = Number(entry.unit_price);
  if (Number.isFinite(packUnit) && packUnit > 0) {
    return packUnit * qty;
  }

  const linePrice = Number(entry.price);
  if (Number.isFinite(linePrice) && linePrice > 0) {
    return linePrice;
  }

  return (Number(entry.product?.price) || 0) * qty;
}

/**
 * Pack unit price for one pack:
 * starts from admin pack_price, then adjusts by size price deltas
 * vs each item's admin unit_price when the customer changes size.
 */
export function getPackUnitTotalFromSelections(pack, selections) {
  const basePackPrice = getPackDisplayPrice(pack);
  const products = getPackIncludedProducts(pack);
  if (products.length === 0) return basePackPrice;

  const delta = products.reduce((sum, entry, index) => {
    const productKey = getPackProductKey(entry, index);
    const selection = selections?.[productKey];
    const qty = Math.max(1, Number(entry.quantity) || 1);
    const adminUnit =
      Number(entry.unit_price) || Number(entry.units?.[0]?.unit_price) || 0;
    const selectedUnit = Number(selection?.unit_price);
    const nextUnit =
      Number.isFinite(selectedUnit) && selectedUnit > 0
        ? selectedUnit
        : adminUnit;
    if (!(adminUnit > 0) || !(nextUnit > 0)) return sum;
    return sum + (nextUnit - adminUnit) * qty;
  }, 0);

  const adjusted = basePackPrice + delta;
  return adjusted > 0 ? adjusted : basePackPrice;
}

export function getPackIncludedProducts(pack) {
  if (!pack) return [];
  if (Array.isArray(pack.pack_products) && pack.pack_products.length > 0) {
    return pack.pack_products;
  }
  if (Array.isArray(pack.products) && pack.products.length > 0) {
    return pack.products;
  }
  return [];
}

export function getPackProductKey(entry, index) {
  return String(entry.id || entry.product_id || entry.product?.id || index);
}

function cleanOptionLabel(value) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed || trimmed === "[object Object]") return "";
  return trimmed;
}

function getSizeLabel(size) {
  if (!size) return "";
  if (typeof size === "string") return cleanOptionLabel(size);

  const raw = size.size;
  if (typeof raw === "string") return cleanOptionLabel(raw);
  if (raw && typeof raw === "object") {
    return (
      cleanOptionLabel(raw.size) ||
      cleanOptionLabel(raw.name) ||
      cleanOptionLabel(raw.label) ||
      cleanOptionLabel(raw.value)
    );
  }

  return cleanOptionLabel(size.name) || cleanOptionLabel(size.label) || "";
}

function getColorLabel(color) {
  if (!color) return "";
  if (typeof color === "string") return cleanOptionLabel(color);
  if (typeof color !== "object") return "";

  const nestedColor = color.color;
  const nestedColour = color.colour;

  return (
    cleanOptionLabel(color.name) ||
    cleanOptionLabel(
      typeof nestedColor === "string" ? nestedColor : nestedColor?.name
    ) ||
    cleanOptionLabel(
      typeof nestedColour === "string" ? nestedColour : nestedColour?.name
    ) ||
    cleanOptionLabel(color.label) ||
    cleanOptionLabel(color.value) ||
    cleanOptionLabel(color.title) ||
    cleanOptionLabel(color.color_name) ||
    cleanOptionLabel(color.colorName) ||
    ""
  );
}

function resolvePackProductSizeId(entry, preferredSizeId, preferredSizeLabel) {
  const sizes = entry.product?.product_sizes;
  if (!Array.isArray(sizes) || sizes.length === 0) return preferredSizeId;

  if (preferredSizeId) {
    const byId = sizes.find(
      (size) => String(size.id) === String(preferredSizeId)
    );
    if (byId?.id) return byId.id;
  }

  const sizeLabel = String(preferredSizeLabel || entry.size || "")
    .split(",")[0]
    ?.trim()
    .toLowerCase();
  if (sizeLabel) {
    const matched = sizes.find(
      (size) => String(size.size || "").trim().toLowerCase() === sizeLabel
    );
    if (matched?.id) return matched.id;
  }

  return sizes[0]?.id;
}

function resolvePackProductSku(entry, preferredSizeId, preferredSizeLabel) {
  const sizes = entry.product?.product_sizes;
  if (!Array.isArray(sizes) || sizes.length === 0) return undefined;

  if (preferredSizeId) {
    const byId = sizes.find(
      (size) => String(size.id) === String(preferredSizeId)
    );
    if (byId?.sku_code) return byId.sku_code;
  }

  const sizeLabel = String(preferredSizeLabel || entry.size || "")
    .split(",")[0]
    ?.trim()
    .toLowerCase();
  if (sizeLabel) {
    const matched = sizes.find(
      (size) => String(size.size || "").trim().toLowerCase() === sizeLabel
    );
    if (matched?.sku_code) return matched.sku_code;
  }

  return sizes[0]?.sku_code || undefined;
}

/** Normalize pack included products for cart + checkout payloads. */
export function mapPackProductsForCart(pack, packQuantity = 1, selections) {
  const multiplier = Math.max(1, Number(packQuantity) || 1);

  return getPackIncludedProducts(pack).map((entry, index) => {
    const productKey = getPackProductKey(entry, index);
    const selection = selections?.[productKey];
    const baseQuantity = Math.max(1, Number(entry.quantity) || 1);
    const selectedSize = entry.product?.product_sizes?.find(
      (size) => String(size.id) === String(selection?.productSizeId || "")
    );
    const unitPrice =
      Number(
        selection?.unit_price ??
          selectedSize?.price ??
          entry.unit_price ??
          entry.price ??
          entry.product?.price ??
          0
      ) || 0;
    const quantity = baseQuantity * multiplier;
    const name = entry.name || entry.product?.name || "Included product";
    const mediaHint =
      [entry.product?.picture, entry.picture, entry.product?.imgURL].find(
        (value) => isUsableImageUrl(value) && isAbsoluteHttpUrl(value)
      ) || null;
    const selectedColorImage = (() => {
      const colors = Array.isArray(entry.product?.color)
        ? entry.product?.color
        : [];
      const normalized = String(selection?.color || entry.color || "")
        .trim()
        .toLowerCase();
      if (!normalized) return undefined;
      const matched = colors.find(
        (color) => getColorLabel(color).toLowerCase() === normalized
      );
      return getColorOptionImage(matched, mediaHint);
    })();
    const picture =
      normalizeMediaUrl(selection?.picture, mediaHint) ||
      selectedColorImage ||
      normalizeMediaUrl(entry.picture, mediaHint) ||
      normalizeMediaUrl(entry.product?.picture, mediaHint) ||
      undefined;
    const color = selection?.color || entry.color || undefined;
    const size =
      selection?.size || getSizeLabel(selectedSize) || entry.size || undefined;
    const inches =
      selection?.inches || selectedSize?.inches || entry.inches || undefined;
    const productSizeId = resolvePackProductSizeId(
      entry,
      selection?.productSizeId,
      size
    );

    return {
      id: String(entry.id || entry.product_id),
      productId: String(entry.product_id || entry.product?.id || entry.id),
      productSizeId,
      name,
      type: "product",
      quantity,
      price: unitPrice,
      unit_price: unitPrice,
      size,
      inches,
      color,
      variety: color,
      sku_code:
        selection?.sku_code ||
        resolvePackProductSku(entry, productSizeId, size),
      picture,
      imageUrl: picture,
      units: Array.isArray(entry.units) ? entry.units : undefined,
    };
  });
}

function normalizePack(raw) {
  if (!raw || typeof raw !== "object") return null;

  const id = raw.id ?? raw.pack_id ?? raw._id;
  if (id === undefined || id === null) return null;

  const name =
    typeof raw.name === "string"
      ? raw.name
      : typeof raw.title === "string"
        ? raw.title
        : "Pack";

  const picture = raw.picture || raw.image || raw.imgURL || raw.image_url || null;
  const packPrice = raw.pack_price ?? raw.price ?? 0;
  const products = Array.isArray(raw.pack_products)
    ? raw.pack_products
    : Array.isArray(raw.products)
      ? raw.products
      : [];

  return {
    ...raw,
    id: String(id),
    name,
    sku_code: raw.sku_code ?? null,
    description: raw.description ?? null,
    image: raw.image || picture,
    picture,
    imgURL: picture || undefined,
    combined_price: Number(raw.combined_price ?? 0),
    discount: Number(raw.discount ?? 0),
    discount_type: raw.discount_type ?? "percentage",
    discount_amount: Number(raw.discount_amount ?? 0),
    pack_price: Number(packPrice),
    price: Number(packPrice),
    status: raw.status !== false,
    sort_order: Number(raw.sort_order ?? 0),
    pack: true,
    products,
    pack_products: Array.isArray(raw.pack_products)
      ? raw.pack_products
      : products,
    product_ids: Array.isArray(raw.product_ids) ? raw.product_ids : [],
    type: raw.type || "pack",
  };
}

export function isPackProduct(item) {
  if (!item || typeof item !== "object") return false;
  return item.pack === true || String(item.type || "").toLowerCase() === "pack";
}

export function mapProductsToPacks(products) {
  if (!Array.isArray(products)) return [];

  return products
    .filter(isPackProduct)
    .map(normalizePack)
    .filter(Boolean)
    .filter((pack) => pack.status !== false)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

function isUsableImageUrl(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  return lower !== "null" && lower !== "undefined" && lower !== "[object object]";
}

function getColorOptionImage(color, baseHint) {
  if (!color || typeof color !== "object") return undefined;
  const candidates = [
    color.productImageUrl,
    color.product_image_url,
    color.picture,
    color.image,
    color.imgURL,
    color.imageUrl,
    color.image_url,
  ];
  for (const candidate of candidates) {
    const normalized = normalizeMediaUrl(
      typeof candidate === "string" ? candidate : undefined,
      baseHint
    );
    if (normalized) return normalized;
  }
  return undefined;
}

function isAbsoluteHttpUrl(value) {
  return Boolean(value && /^https?:\/\//i.test(value.trim()));
}

function getColorOptionLabel(color) {
  if (!color) return "";
  if (typeof color === "string") {
    const trimmed = color.trim();
    return trimmed === "[object Object]" ? "" : trimmed;
  }
  if (typeof color !== "object") return "";
  const nested = color.color ?? color.colour;
  const candidates = [
    color.name,
    typeof nested === "string" ? nested : nested?.name,
    color.label,
    color.value,
    color.title,
    color.color_name,
    color.colorName,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      if (trimmed && trimmed !== "[object Object]") return trimmed;
    }
  }
  return "";
}

function mergeColorOptions(baseHint, ...lists) {
  const byLabel = new Map();

  lists.forEach((list) => {
    const items = Array.isArray(list) ? list : [];
    items.forEach((item) => {
      const label = getColorOptionLabel(item).toLowerCase();
      if (!label) return;

      let nextItem = item;
      if (item && typeof item === "object") {
        const absoluteImage = getColorOptionImage(item, baseHint);
        if (absoluteImage && item.productImageUrl !== absoluteImage) {
          nextItem = { ...item, productImageUrl: absoluteImage };
        }
      }

      const existing = byLabel.get(label);
      if (!existing) {
        byLabel.set(label, nextItem);
        return;
      }

      const existingImage = getColorOptionImage(existing, baseHint);
      const nextImage = getColorOptionImage(nextItem, baseHint);
      if (!existingImage && nextImage) {
        byLabel.set(label, nextItem);
        return;
      }
      if (
        existingImage &&
        nextImage &&
        !isAbsoluteHttpUrl(existingImage) &&
        isAbsoluteHttpUrl(nextImage)
      ) {
        byLabel.set(label, nextItem);
      }
    });
  });

  return Array.from(byLabel.values());
}

function mergeProductCatalogData(packProduct, catalogProduct) {
  if (!catalogProduct) return packProduct;

  const nested = packProduct.product || {};
  const mediaHint =
    [
      catalogProduct.picture,
      catalogProduct.image,
      catalogProduct.imgURL,
      nested.picture,
      packProduct.picture,
    ].find((value) => isUsableImageUrl(value) && isAbsoluteHttpUrl(value)) ||
    null;

  const mergedColors = mergeColorOptions(
    mediaHint,
    catalogProduct.color,
    nested.color,
    packProduct.color
  );
  const nestedSizes = Array.isArray(nested.product_sizes)
    ? nested.product_sizes
    : [];
  const catalogSizes = Array.isArray(catalogProduct.product_sizes)
    ? catalogProduct.product_sizes
    : [];
  const mergedSizes = nestedSizes.length > 0 ? nestedSizes : catalogSizes;

  const picture =
    [
      normalizeMediaUrl(catalogProduct.picture, mediaHint),
      normalizeMediaUrl(catalogProduct.image, mediaHint),
      normalizeMediaUrl(catalogProduct.imgURL, mediaHint),
      normalizeMediaUrl(catalogProduct.image_url, mediaHint),
      normalizeMediaUrl(packProduct.picture, mediaHint),
      normalizeMediaUrl(nested.picture, mediaHint),
    ].find(isUsableImageUrl) || null;

  return {
    ...packProduct,
    name: packProduct.name || catalogProduct.name || nested.name,
    picture,
    product: {
      ...catalogProduct,
      ...nested,
      id: String(nested.id || catalogProduct.id || packProduct.product_id || ""),
      name:
        nested.name ||
        catalogProduct.name ||
        packProduct.name ||
        "Included product",
      picture:
        picture ||
        normalizeMediaUrl(nested.picture, mediaHint) ||
        normalizeMediaUrl(catalogProduct.picture, mediaHint) ||
        null,
      imgURL: normalizeMediaUrl(catalogProduct.imgURL, mediaHint) || picture || undefined,
      image: normalizeMediaUrl(catalogProduct.image, mediaHint) || picture || undefined,
      sku_code: nested.sku_code || catalogProduct.sku_code,
      color:
        mergedColors.length > 0
          ? mergedColors
          : nested.color || catalogProduct.color,
      product_sizes:
        mergedSizes.length > 0
          ? mergedSizes
          : nested.product_sizes || catalogProduct.product_sizes,
    },
  };
}

/** Attach full catalog product details (images/colors/sizes) onto pack line items. */
export function enrichPacksWithCatalogProducts(packs, catalogProducts) {
  if (!Array.isArray(packs) || packs.length === 0) return [];
  if (!Array.isArray(catalogProducts) || catalogProducts.length === 0) {
    return packs;
  }

  const catalogById = new Map();
  catalogProducts.forEach((product) => {
    if (!product || product.id == null) return;
    if (product.pack === true || String(product.type || "").toLowerCase() === "pack") {
      return;
    }
    catalogById.set(String(product.id), product);
  });

  return packs.map((pack) => {
    const included = getPackIncludedProducts(pack).map((entry) => {
      const productId = String(entry.product_id || entry.product?.id || "");
      return mergeProductCatalogData(entry, catalogById.get(productId));
    });

    return {
      ...pack,
      products: included,
      pack_products: included,
    };
  });
}
